import { classifyParam, parseLivePath, schemaForLivePath, snap } from '~/lib/params'
import { getShader } from '~/lib/shaders'
import { fillSourcesOf } from '~/lib/sourceFill'
import { phaseAtPosition } from './evaluate'

// What moves a param; MIDI/OSC will feed `manual`, as the hand does.
export const DRIVES = ['bpm', 'time', 'fft', 'manual']
export const LOOP_MODES = ['loop', 'once', 'pingpong', 'pingpong_loop']
export const DIRECTIONS = ['forward', 'reverse']
export const CURVES = ['linear', 'hold', 'easeIn', 'easeOut', 'easeInOut']
export const BANDS = ['low', 'mid', 'high']

export const DURATION = {
	beat: { minimum: 1 / 16, maximum: 1024, initial: 4 },
	sec: { minimum: 0.05, maximum: 3600, initial: 4 },
}
// A drive with no clock keeps whichever length it was given, for the next switch back.
const ANY_DURATION = { minimum: DURATION.sec.minimum, maximum: DURATION.sec.maximum, initial: DURATION.beat.initial }

/** `fft`: gain on the band's swing around its recent average (0 moves nothing), the release time constant in seconds, and `rest`, the position along in→out the swing centres on. */
export const FFT = {
	gain: { minimum: 0, maximum: 10, initial: 0 },
	fall: { minimum: 0, maximum: 2, initial: 0.25 },
	rest: { minimum: 0, maximum: 1, initial: 0 },
}

/** Does `def` move its param? A `manual` def is settings only. */
export const isDriven = def => !!def && def.drive !== 'manual'

/** The clock a timeline drive reads; null for a drive without one. */
export const timebaseOf = def => (def.drive === 'bpm' ? 'beat' : def.drive === 'time' ? 'sec' : null)

export const durationRange = def => DURATION[timebaseOf(def)] ?? ANY_DURATION

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const round3 = v => Math.round(v * 1000) / 1000
// Fine enough for triplet beat lengths (1/3, 2/3) to survive a round-trip.
const round6 = v => Math.round(v * 1e6) / 1e6
const oneOf = (list, v) => (list.includes(v) ? v : list[0])

/** A loop end on `prop`'s closed domain: a cyclic param's maximum is a full turn, never wrapped onto its minimum as `quantize` would. */
export const quantizeEnd = (prop, v) => clamp(snap(v, prop.step ?? 1), prop.minimum, prop.maximum)

/** A hex colour param: its loop runs over 0…1 and maps the position through a `stops` ramp. */
export const isColorProp = prop => prop?.kind === 'color' && prop.type === 'string'

/** A loop needs a continuous param with a finite domain (its range is absolute on that domain), or a colour. */
export const isLoopable = prop => {
	if (isColorProp(prop)) return true
	if (!classifyParam(prop).continuous) return false
	const { minimum: lo, maximum: hi } = prop
	return Number.isFinite(lo) && Number.isFinite(hi) && hi > lo
}

/** A fresh loop over the param's whole range, one bar long, that moves nothing: paused, and an audio drive at no gain. A colour's ramp holds `color` at both ends. */
export const newLoop = (prop, drive = 'bpm', color) => ({
	drive,
	in: isColorProp(prop) ? 0 : prop.minimum,
	out: isColorProp(prop) ? 1 : prop.maximum,
	...(isColorProp(prop) && { stops: flatRamp(color ?? prop.initial) }),
	duration: DURATION.beat.initial,
	mode: 'loop',
	direction: 'forward',
	run: false,
})

const flatRamp = color => [
	{ offset: 0, color: hexOf(color) },
	{ offset: 1, color: hexOf(color) },
]

const hexOf = v => {
	const m = typeof v === 'string' && /^#?([0-9a-f]{6}(?:[0-9a-f]{2})?)$/i.exec(v.trim())
	return m ? `#${m[1].toLowerCase()}` : '#000000'
}

/** Stops sorted by offset, endpoints pinned at 0 and 1, colours as `#rrggbb` or `#rrggbbaa`; at least two. */
export const conformStops = stops => {
	const raw = Array.isArray(stops) ? stops.filter(s => Number.isFinite(s?.offset)) : []
	if (!raw.length) return flatRamp()
	const out = raw
		.map(s => ({ offset: round3(clamp(s.offset, 0, 1)), color: hexOf(s.color) }))
		.sort((a, b) => a.offset - b.offset)
	if (out.length < 2) out.push({ ...out[0] })
	out[0].offset = 0
	out.at(-1).offset = 1
	return out
}

/** Is `def` still `newLoop`'s, wherever its playhead (phase, rest) sits? A colour's is a flat ramp of any colour. */
export const isNewLoop = (def, prop) => {
	const fresh = newLoop(prop, def.drive, def.stops?.[0]?.color)
	const same =
		['in', 'out', 'duration', 'mode', 'direction'].every(k => def[k] === fresh[k]) &&
		JSON.stringify(def.stops) === JSON.stringify(fresh.stops)
	const still = def.drive === 'fft' ? !def.gain : !def.run
	return same && still && !def.envelope && !def.band && def.fall === undefined
}

// A def from before `drive`: its timebase named the clock.
const driveOf = def => (DRIVES.includes(def.drive) ? def.drive : def.timebase === 'sec' ? 'time' : 'bpm')

const isLinearEnvelope = keys => keys.length === 2 && keys[0].y === 0 && keys[1].y === 1 && keys[0].curve === 'linear'

/** Keys sorted by x, endpoints pinned at x 0 and 1, values in 0…1. Null when the envelope is the identity. */
export const conformEnvelope = env => {
	const raw = Array.isArray(env?.keys) ? env.keys : null
	if (!raw || raw.length < 2) return null
	const keys = raw
		.filter(k => Number.isFinite(k?.x) && Number.isFinite(k?.y))
		.map(k => ({ x: round3(clamp(k.x, 0, 1)), y: round3(clamp(k.y, 0, 1)), curve: oneOf(CURVES, k.curve) }))
		.sort((a, b) => a.x - b.x)
	if (keys.length < 2) return null
	keys[0].x = 0
	keys.at(-1).x = 1
	return isLinearEnvelope(keys) ? null : { keys }
}

/** `def`'s in/out on `prop`'s domain, ordered; a colour's are always 0…1. */
const endsOf = (def, prop) => {
	if (isColorProp(prop)) return [0, 1]
	const end = (v, fallback) => quantizeEnd(prop, Number.isFinite(v) ? v : fallback)
	const a = end(def.in, prop.minimum)
	const b = end(def.out, prop.maximum)
	return a > b ? [b, a] : [a, b]
}

const motionHead = def => {
	const drive = driveOf(def)
	const d = durationRange({ drive })
	return {
		drive,
		duration: round6(clamp(Number.isFinite(def.duration) ? def.duration : d.initial, d.minimum, d.maximum)),
		mode: oneOf(LOOP_MODES, def.mode),
		direction: oneOf(DIRECTIONS, def.direction),
		run: def.run !== false,
	}
}

const motionTail = (out, def, phase) => {
	// 1 is the cycle's far end (out, on a forward loop), not its beginning again.
	const p = round6(clamp(Number.isFinite(phase) ? phase : 0, 0, 1))
	if (p) out.phase = p
	const envelope = conformEnvelope(def.envelope)
	if (envelope) out.envelope = envelope
	// The audio settings are sparse: stored only off their initial, kept across drives.
	if (BANDS.includes(def.band) && def.band !== BANDS[0]) out.band = def.band
	for (const key of ['gain', 'fall', 'rest']) {
		const r = FFT[key]
		if (Number.isFinite(def[key]) && def[key] !== r.initial)
			out[key] = round6(clamp(def[key], r.minimum, r.maximum))
	}
	return out
}

/**
 * One loop in its single stored spelling, or null when it cannot apply to `prop`.
 *
 * ⚠️ `in`/`out` are quantized like the param itself, never pruned: they are positions, not values a preset may omit.
 */
export const conformLoop = (def, prop) => {
	if (!def || typeof def !== 'object' || !isLoopable(prop)) return null
	const [a, b] = endsOf(def, prop)
	const { drive, ...timing } = motionHead(def)
	const out = { drive, in: a, out: b, ...timing }
	if (isColorProp(prop)) out.stops = conformStops(def.stops)
	// A def from before `phase` placed its playhead with `start`, a value along in→out.
	const phase = Number.isFinite(def.phase)
		? def.phase
		: Number.isFinite(def.start) && b > a
			? phaseAtPosition(out, (def.start - a) / (b - a))
			: 0
	return motionTail(out, def, phase)
}

// A drive shared by several params: a driver `@<id>` holds the motion, each param `{ src, in, out, stops? }`.
export const isDriverKey = key => key.startsWith('@')

const MAPPING_KEYS = new Set(['in', 'out', 'stops'])

/** A def (or a patch) split into what a driver shares and what each param maps on its own. */
export const splitDef = def => {
	const motion = {}
	const mapping = {}
	for (const k in def) if (k !== 'src') (MAPPING_KEYS.has(k) ? mapping : motion)[k] = def[k]
	return { motion, mapping }
}

const conformDriver = def => (def && typeof def === 'object' ? motionTail(motionHead(def), def, def.phase) : null)

const conformLink = (def, prop) => {
	if (!isLoopable(prop) || typeof def.src !== 'string' || !isDriverKey(def.src)) return null
	const [a, b] = endsOf(def, prop)
	const out = { src: def.src, in: a, out: b }
	if (isColorProp(prop)) out.stops = conformStops(def.stops)
	return out
}

/** One entry of a `modulators` section conformed on its own — a driver, a link or an inline loop — never collapsing a shared drive. */
export const conformEntry = (key, def, objects) => {
	if (isDriverKey(key)) return conformDriver(def)
	const prop = targetOf(key, objects)
	if (!prop || !def || typeof def !== 'object') return null
	return typeof def.src === 'string' ? conformLink(def, prop) : conformLoop(def, prop)
}

const resolved = new WeakMap()

/** The def that drives `path` as one object: inline, or its driver's motion with the link's mapping over it. */
export const defOf = (section, path) => {
	const def = section?.[path]
	if (!def?.src) return def
	const driver = section[def.src]
	if (!driver) return undefined
	let hit = resolved.get(def)
	if (hit?.driver !== driver) resolved.set(def, (hit = { driver, def: { ...driver, ...def } }))
	return hit.def
}

/** The params a driver drives. */
export const linksOf = (section, src) => Object.keys(section ?? {}).filter(p => section[p]?.src === src)

/** A free driver id named after the param it was shared from: `@<path>`, else `@<path>~2`… */
const driverIdFor = (taken, path) => {
	let id = `@${path}`
	for (let n = 2; taken(id); n++) id = `@${path}~${n}`
	return id
}

/** `section` with `path` driven by `from`'s drive, keeping `mapping` (its in/out) as its own. A drive on `from` alone becomes a driver both share. */
export const linkInto = (section, path, from, mapping) => {
	const source = section?.[from]
	if (!source || from === path) return section
	const next = { ...section }
	let src = source.src
	if (!src) {
		src = driverIdFor(id => !!next[id], from)
		const split = splitDef(source)
		next[src] = split.motion
		next[from] = { src, ...split.mapping }
	}
	next[path] = { src, ...mapping }
	return next
}

/** `section` with `path` off its driver, holding a copy of the drive (with `patch` over it) inline. */
export const detachFrom = (section, path, patch) => {
	const def = defOf(section, path)
	if (!def?.src) return section
	const { src: _src, ...own } = def
	return { ...section, [path]: { ...own, ...patch } }
}

/** The drives `path` could follow, one list of driven params per drive; its own drive left out. */
export const linkTargets = (section, path) => {
	const own = section?.[path]
	const groups = new Map()
	for (const p in section) {
		if (isDriverKey(p) || p === path) continue
		const def = defOf(section, p)
		const key = def?.src ?? p
		if (!isDriven(def) || (own?.src && key === own.src)) continue
		if (!groups.has(key)) groups.set(key, [])
		groups.get(key).push(p)
	}
	return [...groups.values()]
}

// Brush props whose write refits its outline (`objectWrites.js`): a raw per-frame override would draw them wrong. A vector's or group's W/H sample is derived in the sink.
const BRUSH_REFIT_KEYS = new Set(['minWidth', 'widthGrowth', 'smoothness', 'width', 'height'])

const objectKeyLoopable = (obj, key) => !(obj.type === 'brush' && BRUSH_REFIT_KEYS.has(key))

/** Can a loop at `path` apply to this scene? Its param must be loopable and render from a sample; an object's must exist, and a source param must be a shader's own, on a shader some fill shows. */
const targetOf = (path, objects) => {
	const p = parseLivePath(path)
	if (!p || p.existence) return null
	if (p.scope === 'object') {
		const obj = objects?.[p.id]
		if (!obj || !objectKeyLoopable(obj, p.key)) return null
	}
	if (p.scope === 'source' && (!getShader(p.source)?.params?.[p.key] || !fillSourcesOf(objects).has(p.source)))
		return null
	const prop = schemaForLivePath(path, objects)
	// A colour drives only a shader uniform: an object's stroke or fill renders through its own refit.
	if (isColorProp(prop) && p.scope !== 'source') return null
	return isLoopable(prop) ? prop : null
}

/** Can `path` carry a loop in this scene? */
export const canLoop = (path, objects) => !!targetOf(path, objects)

/** A whole `modulators` section conformed against `objects`; null when nothing survives. A driver left with one param goes back inline. */
export const conformModulators = (section, objects) => {
	if (!section || typeof section !== 'object') return null
	const out = {}
	const drivers = new Map()
	for (const path in section) {
		if (isDriverKey(path)) continue
		const raw = section[path]
		const shared = typeof raw?.src === 'string'
		const driver = shared && conformDriver(section[raw.src])
		const def = (!shared || driver) && conformEntry(path, raw, objects)
		if (!def) continue
		out[path] = def
		if (!shared) continue
		if (!drivers.has(def.src)) drivers.set(def.src, { driver, paths: [] })
		drivers.get(def.src).paths.push(path)
	}
	for (const { driver, paths } of drivers.values())
		if (paths.length === 1) {
			const { src: _src, ...mapping } = out[paths[0]]
			out[paths[0]] = conformLoop({ ...driver, ...mapping }, targetOf(paths[0], objects))
		}
	// The engine runs an inline loop on `path` under `@<path>`, so no driver may hold that id.
	const inline = id => out[id.slice(1)] && !out[id.slice(1)].src
	const taken = id => !!out[id] || drivers.has(id) || inline(id)
	for (const [id, { driver, paths }] of drivers) {
		if (paths.length < 2) continue
		const key = inline(id) ? driverIdFor(taken, paths[0]) : id
		out[key] = driver
		if (key !== id) for (const p of paths) out[p] = { ...out[p], src: key }
	}
	return Object.keys(out).length ? out : null
}

/** Drop every loop whose target is gone, in place; delete the section when emptied. The reducers' invariant. */
export const reapModulators = live => {
	const section = live.modulators
	if (!section) return
	const gone = Object.keys(section).filter(path => !isDriverKey(path) && !targetOf(path, live.objects))
	if (!gone.length) return
	for (const path of gone) delete section[path]
	// A driver left driving one param goes back inline.
	const next = conformModulators(section, live.objects)
	if (next) live.modulators = next
	else delete live.modulators
}
